/**
 * SEO Service - Centralized SEO data management for JamPack'd
 * Provides utilities for generating dynamic meta tags, structured data, and SEO optimization
 */

export interface SEOData {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url: string;
  type?: 'website' | 'product' | 'article' | 'organization';
  schema?: object[];
  author?: string;
  siteName?: string;
  locale?: string;
}

export interface ProductSEOData extends SEOData {
  price?: number;
  currency?: string;
  availability?: string;
  condition?: string;
  brand?: string;
  category?: string;
  rating?: number;
  reviewCount?: number;
}

// Base site configuration
const SITE_CONFIG = {
  siteName: 'JamPack\'d Care Packages',
  baseUrl: typeof window !== 'undefined' ? window.location.origin : 'https://jampackd.store',
  defaultImage: '/lovable-uploads/bf2869c6-c7dd-4a46-a046-7189f9435aa3.png',
  description: 'Send a taste of Jamaica to your loved ones with JamPack\'d. Authentic Jamaican snacks, treats, and cultural items delivered worldwide.',
  keywords: ['jamaican food', 'care packages', 'jamaican snacks', 'international shipping', 'caribbean food', 'jamaican treats'],
  author: 'JamPack\'d',
  locale: 'en_US'
};

/**
 * Generate SEO data for the homepage
 */
export const getHomepageSEO = (): SEOData => {
  return {
    title: 'JamPack\'d - Authentic Jamaican Care Packages | Send Jamaica Worldwide',
    description: 'Send a taste of Jamaica to your loved ones with JamPack\'d. Authentic Jamaican snacks, treats, and cultural items delivered worldwide. Shop now!',
    keywords: ['jamaican care packages', 'jamaican food delivery', 'send jamaican snacks', 'caribbean food packages', 'jamaican treats worldwide', 'jamaican snacks', 'jamaican seasonings', 'caribbean care packages', 'caribbean snacks', 'caribbean seasonings'],
    image: SITE_CONFIG.defaultImage,
    url: SITE_CONFIG.baseUrl,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale,
    schema: [
      generateOrganizationSchema(),
      generateWebSiteSchema()
    ]
  };
};

/**
 * Generate SEO data for product listing pages
 */
export const getProductListingSEO = (category?: string, searchQuery?: string): SEOData => {
  let title = 'JamPack\'d Products';
  let description = 'Browse our collection of authentic Jamaican products';
  let url = `${SITE_CONFIG.baseUrl}/products`;

  if (category) {
    title = `${category} - Jamaican ${category} | JamPack'd`;
    description = `Shop authentic Jamaican ${category.toLowerCase()} products. High-quality ${category.toLowerCase()} from Jamaica delivered worldwide.`;
    url = `${SITE_CONFIG.baseUrl}/products/${category}`;
  }

  if (searchQuery) {
    title = `Search Results for "${searchQuery}" | JamPack'd`;
    description = `Find "${searchQuery}" in our collection of authentic Jamaican products. Free worldwide shipping on orders over $85.`;
    url = `${SITE_CONFIG.baseUrl}/products?search=${encodeURIComponent(searchQuery)}`;
  }

  return {
    title,
    description,
    keywords: ['jamaican products', category?.toLowerCase(), 'authentic jamaican', 'caribbean food', 'buy jamaican online'].filter(Boolean),
    image: SITE_CONFIG.defaultImage,
    url,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for About page
 */
export const getAboutSEO = (): SEOData => {
  return {
    title: 'About JamPack\'d - Our Story | Authentic Jamaican Care Packages',
    description: 'Learn about JamPack\'d\'s mission to connect people with authentic Jamaican culture through carefully curated care packages. Founded with love for Jamaica.',
    keywords: ['about jampackd', 'jamaican business', 'our story', 'jamaican culture', 'authentic jamaican products'],
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/about`,
    type: 'article',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale,
    schema: [generateOrganizationSchema()]
  };
};

/**
 * Generate SEO data for FAQ page
 */
export const getFAQSEO = (): SEOData => {
  return {
    title: 'Frequently Asked Questions | JamPack\'d Help Center',
    description: 'Get answers to common questions about JamPack\'d care packages, shipping, customs, returns, and more. Find help with your order.',
    keywords: ['jampackd faq', 'shipping questions', 'jamaican care package help', 'customs information', 'order help'],
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/faq`,
    type: 'article',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Cart page
 */
export const getCartSEO = (): SEOData => {
  return {
    title: 'Shopping Cart | JamPack\'d',
    description: 'Review your Jamaican care package items and proceed to secure checkout. Free shipping on orders over $85.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/cart`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Checkout page
 */
export const getCheckoutSEO = (): SEOData => {
  return {
    title: 'Secure Checkout | JamPack\'d',
    description: 'Complete your order securely with our encrypted checkout. Multiple payment options available.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/checkout`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Privacy Policy page
 */
export const getPrivacyPolicySEO = (): SEOData => {
  return {
    title: 'Privacy Policy | JamPack\'d',
    description: 'Learn how JamPack\'d protects your personal information and data. Our commitment to your privacy and security.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/privacy`,
    type: 'article',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Terms of Service page
 */
export const getTermsOfServiceSEO = (): SEOData => {
  return {
    title: 'Terms of Service | JamPack\'d',
    description: 'Read JamPack\'d terms and conditions for using our service and purchasing care packages.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/terms`,
    type: 'article',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Affiliate Signup page
 */
export const getAffiliateSignupSEO = (): SEOData => {
  return {
    title: 'Join JamPack\'d Affiliate Program | Earn Commission on Jamaican Care Packages',
    description: 'Join our affiliate program and earn commission by promoting authentic Jamaican care packages. Easy signup, competitive rates, and real-time tracking.',
    keywords: ['affiliate program', 'earn money online', 'jamaican affiliate', 'commission program', 'referral program', 'partner with jampackd'],
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/affiliate/signup`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Affiliate Dashboard page
 */
export const getAffiliateDashboardSEO = (): SEOData => {
  return {
    title: 'Affiliate Dashboard | JamPack\'d Partner Portal',
    description: 'Track your affiliate performance, commissions, and referrals in your JamPack\'d partner dashboard.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/affiliate`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for NotFound page (404)
 */
export const getNotFoundSEO = (): SEOData => {
  return {
    title: 'Page Not Found | JamPack\'d Care Packages',
    description: 'Sorry, the page you\'re looking for doesn\'t exist. Browse our authentic Jamaican care packages or return to our homepage.',
    keywords: ['page not found', '404 error', 'jamaican care packages', 'jampackd'],
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/404`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Order Confirmation page
 */
export const getOrderConfirmationSEO = (): SEOData => {
  return {
    title: 'Order Confirmed | JamPack\'d Care Packages',
    description: 'Thank you for your order! Your Jamaican care package order has been confirmed and will be processed shortly.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/order-confirmation`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Settings page
 */
export const getSettingsSEO = (): SEOData => {
  return {
    title: 'Account Settings | JamPack\'d Care Packages',
    description: 'Manage your JamPack\'d account settings, preferences, and profile information.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/settings`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for Order History page
 */
export const getOrderHistorySEO = (): SEOData => {
  return {
    title: 'Order History | JamPack\'d Care Packages',
    description: 'View your JamPack\'d order history and track your Jamaican care package deliveries.',
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}/orders`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate SEO data for utility pages (basic noindex pages)
 */
export const getUtilityPageSEO = (title: string, description: string, path: string): SEOData => {
  return {
    title: `${title} | JamPack\'d Care Packages`,
    description,
    image: SITE_CONFIG.defaultImage,
    url: `${SITE_CONFIG.baseUrl}${path}`,
    type: 'website',
    author: SITE_CONFIG.author,
    siteName: SITE_CONFIG.siteName,
    locale: SITE_CONFIG.locale
  };
};

/**
 * Generate Organization Schema.org JSON-LD
 */
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "JamPack'd Care Packages",
    "description": "Authentic Jamaican care packages and snacks delivered worldwide",
    "url": SITE_CONFIG.baseUrl,
    "logo": `${SITE_CONFIG.baseUrl}${SITE_CONFIG.defaultImage}`,
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": `${SITE_CONFIG.baseUrl}/faq`
    },
    "sameAs": [
      "https://www.instagram.com/jampackdcarepackages",
      "https://www.tiktok.com/@jampackdcarepackages"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "Jamaica"
    }
  };
};

/**
 * Generate WebSite Schema.org JSON-LD with search action
 */
export const generateWebSiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "JamPack'd",
    "description": SITE_CONFIG.description,
    "url": SITE_CONFIG.baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_CONFIG.baseUrl}/products?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

/**
 * Generate Product Schema.org JSON-LD
 */
export const generateProductSchema = (product: any) => {
  const price = product.onSale && product.onSalePrice ? product.onSalePrice : product.price;
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.image || product.images?.[0],
    "url": `${SITE_CONFIG.baseUrl}/products/${product.id}`,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "JamPack'd"
    },
    "offers": {
      "@type": "Offer",
      "price": price.toString(),
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "condition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "JamPack'd"
      }
    },
    "aggregateRating": product.reviews?.length > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": calculateAverageRating(product.reviews),
      "reviewCount": product.reviews.filter((r: any) => r.status === 'verified').length
    } : undefined
  };
};

/**
 * Generate Breadcrumb Schema.org JSON-LD
 */
export const generateBreadcrumbSchema = (breadcrumbs: Array<{name: string, url: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

/**
 * Calculate average rating from reviews
 */
function calculateAverageRating(reviews: any[]): number {
  if (!reviews || reviews.length === 0) return 0;
  const verifiedReviews = reviews.filter(review => review.status === 'verified');
  if (verifiedReviews.length === 0) return 0;
  
  const total = verifiedReviews.reduce((sum, review) => sum + review.rating, 0);
  return Math.round((total / verifiedReviews.length) * 10) / 10;
}

/**
 * Generate canonical URL with proper formatting
 */
export const generateCanonicalUrl = (path: string): string => {
  return `${SITE_CONFIG.baseUrl}${path}`;
};

/**
 * Generate meta keywords string from array
 */
export const generateKeywords = (keywords: string[]): string => {
  return keywords.join(', ');
};

/**
 * Validate and sanitize SEO data
 */
export const validateSEOData = (seoData: SEOData): SEOData => {
  return {
    ...seoData,
    title: seoData.title.length > 60 ? seoData.title.substring(0, 57) + '...' : seoData.title,
    description: seoData.description.length > 160 ? seoData.description.substring(0, 157) + '...' : seoData.description
  };
};